import { JUNGLE_BACKEND_PROD_DOMAIN } from "../../constants/GlobalTypesAndEnumsAndConstants";
import { getAnonymousUploadClaim, rememberAnonymousUpload } from "./anonymous-upload-claims";

// Query params the landing page adds to the handoff so the app can later prove the visitor owns the
// upload. They describe the stored object exactly as the processing task was created with.
export const LANDING_HANDOFF_STORED_FILE_NAME_QUERY_PARAM = "storedFileName";
export const LANDING_HANDOFF_OBJECT_KEY_QUERY_PARAM = "objectKey";
export const LANDING_HANDOFF_CONTENT_MEDIUM_TYPE_QUERY_PARAM = "contentMediumType";

// The upload's private signup claim travels in the URL fragment, not the query (see goToApp).
export const LANDING_HANDOFF_UPLOAD_CLAIM_FRAGMENT_PARAM = "uploadClaim";

export const LANDING_HANDOFF_QUERY_PARAMS = [
  "fileTaskId",
  "fileName",
  LANDING_HANDOFF_STORED_FILE_NAME_QUERY_PARAM,
  LANDING_HANDOFF_OBJECT_KEY_QUERY_PARAM,
  LANDING_HANDOFF_CONTENT_MEDIUM_TYPE_QUERY_PARAM,
];

// The same URL file-upload-client remembers for in-app anonymous uploads; the claim lookup matches on it.
const FILE_PROCESSING_TASK_URL = `${JUNGLE_BACKEND_PROD_DOMAIN}/file_or_url/create_task_to_get_file_data`;

/**
 * An anonymous in-app upload is remembered in this tab (anonymous-upload-claims) so that, once the visitor
 * signs up, the app can claim the upload for their account the first time the backend refuses to serve the
 * source file to them. A landing-page upload happens on jungleai.com, whose storage the app cannot read, so
 * the landing page passes the same details on the handoff URL and they are remembered here instead.
 * Without this, a deck generated from a landing-page upload would lose its source document after sign-up, and
 * since the backend checks the upload's private claim (2026-10-31), generation itself is refused.
 */
export function rememberAnonymousUploadFromLandingHandoff(handoffParams: URLSearchParams, handoffFragmentParams: URLSearchParams): void {
  const fileTaskId = handoffParams.get("fileTaskId");
  const storedFileName = handoffParams.get(LANDING_HANDOFF_STORED_FILE_NAME_QUERY_PARAM);
  const uploadedFileS3ObjectKey = handoffParams.get(LANDING_HANDOFF_OBJECT_KEY_QUERY_PARAM);
  const contentMediumType = handoffParams.get(LANDING_HANDOFF_CONTENT_MEDIUM_TYPE_QUERY_PARAM);
  if (!fileTaskId || !storedFileName || !uploadedFileS3ObjectKey || !contentMediumType) return;
  const fragmentUploadClaimToken = handoffFragmentParams.get(LANDING_HANDOFF_UPLOAD_CLAIM_FRAGMENT_PARAM);
  // The fragment is removed from the address bar on arrival, but the router can still hold the handoff query:
  // a remount handles the same upload again without it, and must keep the claim already remembered.
  const uploadClaimToken = fragmentUploadClaimToken ||
    getAnonymousUploadClaim(fileTaskId, FILE_PROCESSING_TASK_URL)?.body.upload_claim_token;
  rememberAnonymousUpload(fileTaskId, FILE_PROCESSING_TASK_URL, {
    file_name: storedFileName,
    uploaded_file_s3_object_key: uploadedFileS3ObjectKey,
    content_medium_type: contentMediumType,
    // Without it the backend refuses to generate from the upload (403): it was created anonymously with this
    // claim, and only a request presenting the claim, or the account that already claimed it, may read it.
    ...(uploadClaimToken && /^[a-f0-9]{64}$/.test(uploadClaimToken) ? { upload_claim_token: uploadClaimToken } : {}),
  });
}
